import type { SeoPageConfig } from '../types/Seo';
import { CONFERENCES_TIMING_CONTENT } from '../content/useCaseTimingContent';
import { PRODUCTION_TIMING_CONTENT } from '../content/productionTimingContent';
import { MEETING_TIMER_CONTENT } from '../content/meetingTimerContent';
import { EDUCATION_TIMER_CONTENT } from '../content/educationTimerContent';
import { STREAM_TIMER_CONTENT } from '../content/streamTimerContent';
import { BROADCAST_TIMER_CONTENT } from '../content/broadcastTimerContent';
import { WORKOUT_TIMER_CONTENT } from '../content/workoutTimerContent';
import { TRADE_SHOW_TIMER_CONTENT } from '../content/tradeShowTimerContent';
import { WEBINAR_TIMER_CONTENT } from '../content/webinarTimerContent';
import { CHURCH_TIMER_CONTENT } from '../content/churchTimerContent';
import { PRESENTATIONS_FAQ_ITEMS } from '../content/presentationsFaqContent';
import {
  buildFaqPageSchema,
  buildFaqPageSchemaFromItems,
  buildHomePageJsonLd,
  buildPricingPageSchema,
  buildUseCasePageBreadcrumbSchema,
  FEATURES_PAGE_SOFTWARE_SCHEMA,
  landingSoftwareApplicationSchema,
} from './schemas';

function jsonLdGraph(
  siteUrl: string,
  page: SeoPageConfig,
  ...nodes: Record<string, unknown>[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...nodes.map((n) => ({ ...n, '@context': 'https://schema.org' })),
      buildUseCasePageBreadcrumbSchema(siteUrl, page.path, page.h1),
    ],
  };
}

/** JSON-LD для `<script id="seo-schema-page">` на индексируемых маркетинговых маршрутах. */
export function resolvePageJsonLd(page: SeoPageConfig, siteUrl: string): Record<string, unknown> | null {
  switch (page.path) {
    case '/':
      return buildHomePageJsonLd(siteUrl, page.schema ?? landingSoftwareApplicationSchema);
    case '/features':
      return jsonLdGraph(
        siteUrl,
        page,
        page.schema ?? FEATURES_PAGE_SOFTWARE_SCHEMA,
      );
    case '/pricing':
      return jsonLdGraph(siteUrl, page, buildPricingPageSchema(siteUrl));
    case '/faq':
      return jsonLdGraph(siteUrl, page, buildFaqPageSchema());
    case '/changelog':
      return buildUseCasePageBreadcrumbSchema(siteUrl, page.path, page.h1);
    case '/guides':
      return buildUseCasePageBreadcrumbSchema(siteUrl, page.path, page.h1);
    case '/use-cases/conferences':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(CONFERENCES_TIMING_CONTENT.faqItems),
      );
    case '/use-cases/presentations':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(PRESENTATIONS_FAQ_ITEMS),
      );
    case '/use-cases/timer-event-production':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(PRODUCTION_TIMING_CONTENT.faqItems),
      );
    case '/use-cases/meeting-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(MEETING_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/online-timer-for-education':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(EDUCATION_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/stream-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(STREAM_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/broadcast-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(BROADCAST_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/workout-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(WORKOUT_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/trade-show-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(TRADE_SHOW_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/webinar-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(WEBINAR_TIMER_CONTENT.faqItems),
      );
    case '/use-cases/church-timer':
      return jsonLdGraph(
        siteUrl,
        page,
        buildFaqPageSchemaFromItems(CHURCH_TIMER_CONTENT.faqItems),
      );
    default:
      if (page.path.startsWith('/guides/') || page.path.startsWith('/compare/')) {
        return buildUseCasePageBreadcrumbSchema(siteUrl, page.path, page.h1);
      }
      return null;
  }
}
